import { Button, Calendar } from "@/components";
import { useDesignMaxTheme, useModal } from "@/packages";
import { useNavigation } from "@/packages/navigation/hooks";
import { FONT } from "@/styles";
import { Box, Center, Text } from "@chakra-ui/react";
import { HEADER_GROUP, useCheckFakeValue, useCheckInValue, useCheckOutValue, useNewNavigate } from "@shared/client";
import dayjs from "dayjs";
import { useLocation } from "react-router-dom";
import { type DateSearchBarProps } from "./dateSearchBar.type";
import { handleWeekDay } from "./handlers";
import { useHandleData } from "./hooks";

export const DateSearchBar = ({ isSubMenu }: DateSearchBarProps) => {
    const checkIn = useCheckInValue();
    const checkOut = useCheckOutValue();
    const checkFake = useCheckFakeValue();
    const { navigation } = useNavigation();
    const { onNavigate } = useNewNavigate();
    const { openModal, setModalContent, setIconName } = useModal();
    const { mutate } = useHandleData();
    const { pathname } = useLocation();
    const { COLORS, MEDIAS } = useDesignMaxTheme();
    const { isMobile, isTablet, isLaptop } = MEDIAS();
    const isReservePage = pathname.includes("/reserve");
    const diff = dayjs(checkOut).diff(dayjs(checkIn), "day");

    const handleData = () => {
        if (!isReservePage) {
            onNavigate({ path: "/reserve" });
        }

        if (isReservePage) {
            mutate({ checkIn, checkOut: checkOut ?? checkFake });
        }
    };

    const onModal = () => {
        openModal();
        setIconName("close");
        setModalContent(<Calendar onNavigate={onNavigate} />);
    };

    const { roomList, specialList } = navigation;
    const mobileColumnGap = isMobile ? "24px" : "60px";

    return (
        <Center w="100%" position={isSubMenu ? "absolute" : "relative"} bottom="0" h={isMobile ? "80px" : "120px"}>
            <Center
                position={isReservePage ? "relative" : "initial"}
                className="date_search_bar_container"
                w="100%"
                h="100%"
                color={isReservePage ? COLORS.black : COLORS.white}
                background={isReservePage ? COLORS.white : COLORS.transparent.b40}
                border={isReservePage ? `1px solid ${COLORS.gray[300]}` : "none"}
                p={!isReservePage && isMobile ? "0 16px" : "none"}
            >
                <Center
                    w="100%"
                    columnGap={pathname.includes("/reserve") ? "12px" : mobileColumnGap}
                    onClick={onModal}
                    cursor="pointer"
                >
                    <Box textAlign="center">
                        <Text sx={{ ...FONT("cormorant", 20, 700) }}>Check-in</Text>
                        <Text>
                            {checkIn} ({handleWeekDay(checkIn)})
                        </Text>
                    </Box>
                    <Center
                        textAlign="center"
                        className="dayNight"
                        color={isReservePage ? COLORS.black : COLORS.white}
                        w="48px"
                        h="48px"
                        p="0"
                        border={isReservePage ? `1px solid ${COLORS.gray[300]}` : `1px solid ${COLORS.white}`}
                    >
                        {checkOut ? diff : 1}박
                    </Center>
                    <Box textAlign="center">
                        <Text sx={{ ...FONT("cormorant", 20, 700) }}>Check-out</Text>
                        <Text>
                            {checkOut ?? checkFake} ({handleWeekDay(checkOut ?? checkFake)})
                        </Text>
                    </Box>
                </Center>
                <Box
                    className="date_search_bar_button_box"
                    position={isReservePage ? "absolute" : "initial"}
                    right={isReservePage ? "0" : "auto"}
                    p="12px 80px"
                    borderLeft={isReservePage ? `1px dotted ${COLORS.black}` : "1px dotted white"}
                    display={isTablet ? "none" : "block"}
                    sx={{
                        ...(isReservePage
                            ? {
                                  "& button": {
                                      color: COLORS.white,
                                      bgColor: COLORS.black,
                                      ...FONT("pretendard", 16, 500),
                                      "&:hover": {
                                          bgColor: COLORS.gray[300],
                                      },
                                  },
                              }
                            : {
                                  "& button": {
                                      color: COLORS.white,
                                      border: "1px solid",
                                      borderColor: COLORS.white,
                                  },
                              }),
                    }}
                >
                    <Button size="medium" onClick={handleData}>
                        예약 조회
                    </Button>
                </Box>
            </Center>
            {isSubMenu && (
                <Center
                    display={isLaptop ? "none" : "flex"}
                    className="sub_menu_container"
                    background="white"
                    w="60%"
                    h="100%"
                >
                    {HEADER_GROUP.map((header, index) => (
                        <Center
                            w="25%"
                            key={header.title}
                            flexDir="column"
                            rowGap="4px"
                            borderRight="1px dotted gray"
                            onClick={() => {
                                if (header.title === "Specials") {
                                    onNavigate({ path: `${header.contents[0].path}/${specialList[0].specialIdx}` });
                                }

                                if (header.title === "Rooms") {
                                    onNavigate({ path: `${header.contents[0].path}/${roomList[0].roomIdx}` });
                                }

                                if (header.title !== "Specials" && header.title !== "Rooms") {
                                    onNavigate({ path: header.contents[0].path });
                                }
                            }}
                            cursor="pointer"
                        >
                            <Text fontSize="14px" color={COLORS.blue[200]}>
                                {(index + 1).toString().padStart(2, "0")}
                            </Text>
                            <Text {...FONT("cormorant", 20, 700, 1.2)}>{header.title}</Text>
                            <Text {...FONT("pretendard", 14, 300)}>{header.subTitle}</Text>
                        </Center>
                    ))}
                </Center>
            )}
        </Center>
    );
};
